/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { getServiceListingTypes, serviceSearchQuery } from '../../../../util/serviceSearch';
import { smoothScrollBehavior } from '../../../../util/prefersReducedMotion';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import { getAriaCurrent, getLinkAriaCurrent } from '../currentNavItem';

import css from './TopbarMobileMenu.module.css';

// Identifies the hosted "Найти задачи" link by its exact query contract.
// Matching is intentionally on the query parameter (not path, label or index)
// so hosted-config wording or ordering changes don't break detection. Mirrors
// the desktop contract in TopbarDesktop without sharing a helper (mobile-only).
const isTaskSearchLink = link =>
  typeof link?.href === 'string' && link.href.includes('pub_listingType=tasks');

// Canonical draft listing identifiers used to start the listing-creation wizard.
// Duplicated locally on purpose: no reusable exported canonical helper exists
// and routeConfiguration must not export new constants for this patch.
const CREATE_TASK_PARAMS = {
  slug: 'draft',
  id: '00000000-0000-0000-0000-000000000000',
  type: 'new',
  tab: 'details',
};

// Section id rendered by SectionCategoryDirectory on LandingPage. Kept local
// (not imported) since no shared constant exists for this id.
const ALL_CATEGORIES_ID = 'all-categories';
// Frame budget for the cross-route retry loop below (~0.5s at 60fps), covering
// the time LandingPage needs to mount after navigating from another route.
const MAX_HASH_SCROLL_FRAMES = 30;

// Clicking "Все категории" from the mobile hamburger keeps the existing
// NamedLink navigation to `/#all-categories` untouched. That navigation alone
// is not enough on mobile: closing the TopbarMobileMenu Modal unlocks Page
// scrolling, and Page.js restores the pre-Modal scroll position with a single
// requestAnimationFrame, which can run after (and override) the hash scroll
// Routes.js already performs. Scheduling this scroll after Page's restore
// (double rAF) lets it win that race without touching Routes/Modal/Page.
const scrollToAllCategoriesAfterModalCleanup = () => {
  if (typeof document === 'undefined' || typeof requestAnimationFrame !== 'function') {
    return;
  }

  const tryScroll = () => {
    const target = document.getElementById(ALL_CATEGORIES_ID);

    if (!target) {
      return false;
    }

    target.scrollIntoView({
      block: 'start',
      behavior: smoothScrollBehavior(),
    });

    return true;
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (tryScroll()) {
        return;
      }

      let frameCount = 0;

      const retry = () => {
        if (tryScroll()) {
          return;
        }

        frameCount += 1;

        if (frameCount >= MAX_HASH_SCROLL_FRAMES) {
          return;
        }

        requestAnimationFrame(retry);
      };

      requestAnimationFrame(retry);
    });
  });
};

const CustomLinkComponent = ({ linkConfig, currentNav }) => {
  const { text, type, href, route } = linkConfig;
  const ariaCurrent = getLinkAriaCurrent(linkConfig, currentNav);

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, { [css.currentPage]: ariaCurrent });
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to} aria-current={ariaCurrent}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

// Renders the hosted task-search ("Найти задачи") link as a secondary action
// button. Reuses the hosted label and resolved route/href (no duplicated URL
// contract, no hardcoded translated text) but styles it as the outlined
// secondary CTA. Returns null when the hosted link is absent. On the task search it carries
// aria-current only: a button is an action, no visual mark (backlog #9, as «Все услуги»).
const TaskSearchAction = ({ taskSearchLink, currentNav }) => {
  if (!taskSearchLink) {
    return null;
  }
  const { text, type, href, route } = taskSearchLink;
  if (type === 'internal' && route) {
    const { name, params, to } = route;
    return (
      <NamedLink
        className={css.secondaryActionButton}
        name={name}
        params={params}
        to={to}
        aria-current={getLinkAriaCurrent(taskSearchLink, currentNav)}
      >
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink className={css.secondaryActionButton} href={href}>
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {Object} props.currentNav the open page (currentNavItem.getCurrentNav)
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentNav,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
  } = props;
  // The unfiltered service search of the primary action: every service listing type of the
  // runtime config, tasks excluded (D22, T15). Same query as the desktop primary CTA.
  const findServicesSearch = serviceSearchQuery(getServiceListingTypes(useConfiguration()));

  const user = ensureCurrentUser(currentUser);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isCustomer = userType === 'customer';
  const isProvider = userType === 'provider';
  // Client uses "Мои задачи"; provider and authenticated-but-unresolved use the
  // combined "Мои задачи и услуги" label (safe fallback, never provider CTAs).
  const manageListingsLabelId = isCustomer
    ? 'TopbarMobileMenu.yourListingsLinkClient'
    : 'TopbarMobileMenu.yourListingsLinkProvider';

  // Defensive normalization; hosted customLinks and each link object are never
  // mutated. About/Blog are never identified by label, index or hardcoded URL —
  // only the task-search link is singled out by its query contract.
  const sourceCustomLinks = Array.isArray(customLinks) ? customLinks : [];
  const taskSearchLink = sourceCustomLinks.find(isTaskSearchLink);
  const ordinaryHostedLinks = sourceCustomLinks.filter(link => !isTaskSearchLink(link));

  const ordinaryHostedItems = ordinaryHostedLinks.map((linkConfig, index) => (
    <CustomLinkComponent
      key={`${linkConfig.text}_${index}`}
      linkConfig={linkConfig}
      currentNav={currentNav}
    />
  ));

  // D33: the badge counts "your move" transactions (notificationCount), the same
  // turn-aware source as the topbar dots and the inbox row dots, on every page.
  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  // Current-page indicator (backlog #9): the same resolver as the desktop bar, so both agree.
  const ariaCurrent = (name, params) => getAriaCurrent({ name, params }, currentNav);
  const currentPageClass = (name, params) => (ariaCurrent(name, params) ? css.currentPage : null);

  // --- Action buttons (anchors outside the unified <ul>) ---
  // «Все услуги» on the service search: aria-current only, no visual mark (backlog #9, Q2).
  const findServicesTo = { search: findServicesSearch };
  const findServicesAction = (
    <NamedLink
      className={css.createNewListingLink}
      name="SearchPage"
      to={findServicesTo}
      aria-current={getAriaCurrent({ name: 'SearchPage', to: findServicesTo }, currentNav)}
    >
      <FormattedMessage id="TopbarDesktop.findServices" />
    </NamedLink>
  );
  const createServiceAction = (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  );
  const createTaskAction = (
    <NamedLink
      className={css.secondaryActionButton}
      name="EditListingPage"
      params={CREATE_TASK_PARAMS}
      to={{ search: '?type=tasks' }}
    >
      <FormattedMessage id="TopbarMobileMenu.newTaskLink" />
    </NamedLink>
  );
  const taskSearchAction = (
    <TaskSearchAction taskSearchLink={taskSearchLink} currentNav={currentNav} />
  );

  // --- Navigation <li> items (direct children of the single <ul>) ---
  // The scroll helper doesn't preventDefault/stopPropagation or implement custom
  // navigation; it only schedules the post-cleanup scroll alongside the existing
  // NamedLink navigation (A1-16: on the link itself, not on the <li>).
  const allCategoriesItem = (
    <li key="all-categories" className={css.navigationLink}>
      <NamedLink
        name="LandingPage"
        to={{ hash: '#all-categories' }}
        onClick={scrollToAllCategoriesAfterModalCleanup}
      >
        <FormattedMessage id="TopbarDesktop.viewAllCategories" />
      </NamedLink>
    </li>
  );
  const signupItem = (
    <li key="signup" className={classNames(css.navigationLink, currentPageClass('SignupPage'))}>
      <NamedLink name="SignupPage" aria-current={ariaCurrent('SignupPage')}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    </li>
  );
  const loginItem = (
    <li key="login" className={classNames(css.navigationLink, currentPageClass('LoginPage'))}>
      <NamedLink name="LoginPage" aria-current={ariaCurrent('LoginPage')}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    </li>
  );
  const inboxItem = (
    <li
      key="inbox"
      className={classNames(css.inbox, currentPageClass('InboxPage', { tab: inboxTab }))}
    >
      <NamedLink
        name="InboxPage"
        params={{ tab: inboxTab }}
        aria-current={ariaCurrent('InboxPage', { tab: inboxTab })}
      >
        <FormattedMessage id="TopbarMobileMenu.inboxLink" />
        {notificationCountBadge}
      </NamedLink>
    </li>
  );
  const manageListingsItem = (
    <li
      key="manage-listings"
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
    >
      <NamedLink name="ManageListingsPage" aria-current={ariaCurrent('ManageListingsPage')}>
        <FormattedMessage id={manageListingsLabelId} />
      </NamedLink>
    </li>
  );
  const profileSettingsItem = (
    <li
      key="profile-settings"
      className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
    >
      <NamedLink name="ProfileSettingsPage" aria-current={ariaCurrent('ProfileSettingsPage')}>
        <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
      </NamedLink>
    </li>
  );
  const accountSettingsItem = (
    <li
      key="account-settings"
      className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
    >
      <NamedLink name="AccountSettingsPage" aria-current={ariaCurrent('AccountSettingsPage')}>
        <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
      </NamedLink>
    </li>
  );

  // Role-based composition of the actions group (primary + optional secondary)
  // and the single unified navigation list.
  let primaryAction;
  let secondaryAction;
  let navItems;
  if (!isAuthenticated) {
    // Guest: find services (primary) + find tasks (secondary); nav: signup,
    // login, all categories, then hosted About/Blog in Console order.
    primaryAction = findServicesAction;
    secondaryAction = taskSearchAction;
    navItems = [signupItem, loginItem, allCategoriesItem, ...ordinaryHostedItems];
  } else if (isProvider) {
    // Provider: create service (primary) + find tasks (secondary); nav: inbox,
    // listings, profile, account, then hosted links. No categories/create-task.
    primaryAction = createServiceAction;
    secondaryAction = taskSearchAction;
    navItems = [
      inboxItem,
      manageListingsItem,
      profileSettingsItem,
      accountSettingsItem,
      ...ordinaryHostedItems,
    ];
  } else if (isCustomer) {
    // Client: find services (primary) + create task (secondary); nav: inbox,
    // listings, categories, profile, account, then hosted links.
    primaryAction = findServicesAction;
    secondaryAction = createTaskAction;
    navItems = [
      inboxItem,
      manageListingsItem,
      allCategoriesItem,
      profileSettingsItem,
      accountSettingsItem,
      ...ordinaryHostedItems,
    ];
  } else {
    // Authenticated, unresolved userType (safe fallback): find services only,
    // no secondary; never treat as provider. Nav mirrors the client list with
    // the combined listings label.
    primaryAction = findServicesAction;
    secondaryAction = null;
    navItems = [
      inboxItem,
      manageListingsItem,
      allCategoriesItem,
      profileSettingsItem,
      accountSettingsItem,
      ...ordinaryHostedItems,
    ];
  }

  const greetingBlock = isAuthenticated ? (
    <>
      <span className={css.greeting}>
        <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
      </span>
      <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
        <FormattedMessage id="TopbarMobileMenu.logoutLink" />
      </InlineTextButton>
    </>
  ) : (
    <span className={css.greeting}>
      <FormattedMessage id="TopbarMobileMenu.guestGreeting" />
    </span>
  );

  return (
    <div className={css.root}>
      {isAuthenticated ? <AvatarLarge className={css.avatar} user={currentUser} /> : null}
      <div className={css.content}>
        {greetingBlock}

        <div className={css.actions}>
          {primaryAction}
          {secondaryAction}
        </div>

        <ul className={css.customLinksWrapper}>{navItems}</ul>
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
